import { navigate } from '../route.ts';
import { LEGAL_DOCUMENTS, type LegalDocumentId } from './documents.ts';

/**
 * The three documents, as a list of links, wherever they need listing.
 *
 * One component because there are three places that list them — the landing
 * page footer, the sign-in screen, and each document pointing at the other two
 * — and three hand-written lists would be three chances for one to be missing a
 * document that exists, which is a legal page that has been published and never
 * linked to.
 *
 * **Real `href`s, intercepted.** A person answering a questionnaire wants to
 * copy the address, and somebody reading the terms wants to open the DPA in a
 * second tab beside them. Both need the link to be a link: the interception
 * gives the ordinary click a client-side navigation with no reload, and stands
 * out of the way of every modified click, which the browser then handles as it
 * would on any other site.
 */

interface LegalLinksProps {
  /** The document being read, dropped from the list. Absent when none is. */
  readonly omit?: LegalDocumentId;
  readonly className?: string;
  /** Names the list for a screen reader; the three call it three things. */
  readonly label: string;
}

export const LegalLinks = ({ omit, className, label }: LegalLinksProps) => (
  <nav className={className ?? 'legal-links'} aria-label={label}>
    <ul className="legal-links-list">
      {LEGAL_DOCUMENTS.filter((entry) => entry.id !== omit).map((entry) => (
        <li key={entry.id}>
          <a
            href={entry.path}
            onClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              navigate(entry.path);
            }}
          >
            {entry.title}
          </a>
        </li>
      ))}
    </ul>
  </nav>
);
